import { useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import echo from '../lib/echo'
import type { PipelineStage } from '../types/api'

interface PipelineStageChangedEvent {
  lead_id: string
  from_stage: PipelineStage | null
  to_stage: PipelineStage
  triggered_by: string
  user_id: string | null
  note: string | null
  metadata: Record<string, unknown> | null
  timestamp: string
}

/**
 * Hook for subscribing to real-time pipeline stage updates via Reverb.
 * Per AD-005: Laravel Reverb pushes immediate updates to the browser.
 *
 * @param leadId - Optional lead ID to subscribe to specific lead updates
 * @param onStageChange - Optional callback when stage changes
 */
export function usePipelineUpdates(
  leadId?: string,
  onStageChange?: (event: PipelineStageChangedEvent) => void
) {
  const queryClient = useQueryClient()

  const handleStageChange = useCallback(
    (event: PipelineStageChangedEvent) => {
      // Invalidate relevant queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['lead', event.lead_id] })
      queryClient.invalidateQueries({ queryKey: ['application', event.lead_id] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-metrics'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline'] })

      // Call custom handler if provided
      onStageChange?.(event)
    },
    [queryClient, onStageChange]
  )

  useEffect(() => {
    if (!echo) {
      return
    }
    const realtime = echo

    // Subscribe to general pipeline channel for all updates
    const pipelineChannel = realtime.private('pipeline')
    pipelineChannel.listen('.stage.changed', handleStageChange)

    // If a specific lead ID is provided, also subscribe to that lead's channel
    let leadChannel: ReturnType<typeof realtime.private> | null = null
    if (leadId) {
      leadChannel = realtime.private(`lead.${leadId}`)
      leadChannel.listen('.stage.changed', handleStageChange)
    }

    // Cleanup subscriptions on unmount
    return () => {
      pipelineChannel.stopListening('.stage.changed')
      realtime.leave('pipeline')

      if (leadChannel && leadId) {
        leadChannel.stopListening('.stage.changed')
        realtime.leave(`lead.${leadId}`)
      }
    }
  }, [leadId, handleStageChange])
}

export type { PipelineStageChangedEvent }
